import { useEffect, useState } from "react";
import { Bookmark, X } from "lucide-react";
import { toast } from "sonner";
import { gapi, errMsg } from "../api";
import { useGAuth } from "../GAuth";

export function SavedFilters({ pagina, current, onApply }) {
  const { user } = useGAuth();
  const [list, setList] = useState([]);
  const load = () => gapi.get("/saved-filters", { params: { pagina } }).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, [pagina]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    const nome = window.prompt("Nome del filtro");
    if (!nome) return;
    const condiviso = user?.ruolo === "titolare" && window.confirm("Condividere questo filtro con tutti i collaboratori?");
    try { await gapi.post("/saved-filters", { pagina, nome, filtri: current, condiviso }); load(); toast.success("Filtro salvato"); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { await gapi.delete(`/saved-filters/${id}`); load(); };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`saved-filters-${pagina}`}>
      {list.map((f) => (
        <span key={f.id} className="inline-flex items-center rounded-full bg-accent-subtle text-xs">
          <button className="pl-3 pr-1 py-1.5 font-medium" onClick={() => onApply(f.filtri)} data-testid="saved-filter-apply">{f.nome}{f.condiviso ? " · condiviso" : ""}</button>
          {(f.mio || user?.ruolo === "titolare") && <button className="pr-2 text-anthracite/40 hover:text-red-600" onClick={() => del(f.id)} aria-label="Elimina filtro"><X className="h-3 w-3" /></button>}
        </span>
      ))}
      <button onClick={save} className="inline-flex items-center gap-1 text-xs font-semibold text-brand" data-testid={`save-filter-${pagina}`}><Bookmark className="h-3.5 w-3.5" /> Salva filtro</button>
    </div>
  );
}

export function Pager({ data, page, setPage }) {
  if (!data) return null;
  const pages = Math.max(1, Math.ceil(data.total / data.size));
  return (
    <div className="flex items-center justify-between mt-4 text-sm">
      <span className="text-anthracite/50" data-testid="pager-total">{data.total} risultati</span>
      <span className="flex items-center gap-2">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-full border border-border-subtle px-3 h-9 disabled:opacity-40">Precedente</button>
        <span>{page} / {pages}</span>
        <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-full border border-border-subtle px-3 h-9 disabled:opacity-40">Successiva</button>
      </span>
    </div>
  );
}

export function downloadCsv(path, params, filename) {
  return gapi.get(path, { params, responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }).catch((e) => toast.error(e?.response?.status === 403 ? "Non hai il permesso di esportare" : "Esportazione non riuscita"));
}

export function ContactPicker({ value, onChange, placeholder = "Cerca cliente…", testid = "contact-picker" }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => gapi.get("/contacts-options", { params: { q } }).then((r) => setOpts(r.data)).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <input value={open ? q : value?.displayName || q} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder={placeholder}
        className="w-full h-11 rounded-xl border border-border-subtle bg-white px-3 text-sm outline-none focus:border-brand" data-testid={testid} />
      {open && opts.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-border-subtle bg-white shadow-lg">
          {opts.map((o) => (
            <li key={o.id}><button type="button" onMouseDown={() => { onChange(o); setQ(""); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-accent-subtle" data-testid={`${testid}-opt`}>
              <b>{o.displayName}</b> <span className="text-xs text-anthracite/45">{(o.telefoni || [])[0]?.numero}</span></button></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Field({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="block text-xs font-semibold text-anthracite/55 mb-1">{label}</span>{children}</label>;
}

export function Chips({ options, value = [], onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {Object.entries(options).map(([k, l]) => {
        const on = value.includes(k);
        return <button type="button" key={k} onClick={() => onChange(on ? value.filter((x) => x !== k) : [...value, k])}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${on ? "bg-brand text-white border-brand" : "bg-white border-border-subtle hover:bg-accent-subtle"}`} data-testid={testid ? `${testid}-${k}` : undefined}>{l}</button>;
      })}
    </div>
  );
}
